import { useEffect, useState, type ReactNode } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ChevronLeft, ShieldAlert, Gavel, Flag, Users, Ban } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { fetchUserRisk, type UserRisk as UserRiskData } from './userRiskApi'

/**
 * A3.22 — Risk skori va signal'lar. Mavjud signallardan (aktiv strayk ballari,
 * unga qarshi shikoyatlar, qurilma baham ko'rgan akkauntlar, blok holati) BITTA
 * qaror-yordam ballini jamlaydi. Read-only, migratsiyasiz. Ball qanday
 * hisoblanganини signal-breakdown ko'rsatadi (qora quti emas).
 */

const S = {
  title: 'Risk skori',
  back: 'Karta',
  score: 'Risk balli',
  level: 'Daraja',
  low: 'Past',
  medium: "O'rta",
  high: 'Yuqori',
  signals: 'Signallar',
  strikePoints: 'Aktiv strayk ballari',
  reportsTotal: 'Jami shikoyatlar (unga qarshi)',
  reportsPending: 'Ochiq shikoyatlar',
  related: 'Bog‘liq akkauntlar (qurilma)',
  banned: 'Bloklangan',
  yes: 'Ha',
  no: "Yo'q",
  note: 'Ball = mavjud signallar yig‘indisi (0–100). Faqat ko‘rsatkich — avtomatik amal yo‘q.',
}

const LEVEL_LABEL: Record<string, string> = { low: S.low, medium: S.medium, high: S.high }
const LEVEL_TONE: Record<string, string> = {
  low: 'text-emerald-400 border-emerald-500/30 bg-emerald-500/10',
  medium: 'text-amber-400 border-amber-500/30 bg-amber-500/10',
  high: 'text-rose-400 border-rose-500/30 bg-rose-500/10',
}
const RING: Record<string, string> = { low: '#34d399', medium: '#fbbf24', high: '#fb7185' }

function Gauge({ score, level }: { score: number; level: string }) {
  const r = 52
  const c = 2 * Math.PI * r
  const pct = Math.max(0, Math.min(100, score)) / 100
  return (
    <div className="relative h-32 w-32 shrink-0">
      <svg viewBox="0 0 128 128" className="h-32 w-32 -rotate-90">
        <circle cx="64" cy="64" r={r} fill="none" stroke="currentColor" strokeWidth="10" className="text-border-subtle" />
        <circle
          cx="64" cy="64" r={r} fill="none" stroke={RING[level] ?? '#fbbf24'} strokeWidth="10"
          strokeLinecap="round" strokeDasharray={c} strokeDashoffset={c * (1 - pct)}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-3xl font-semibold text-primary">{score}</span>
        <span className="text-xs text-tertiary">/ 100</span>
      </div>
    </div>
  )
}

function SignalRow({ icon, label, value }: { icon: ReactNode; label: string; value: ReactNode }) {
  return (
    <div className="flex items-center justify-between border-b border-border-subtle py-2 last:border-0">
      <span className="flex items-center gap-2 text-sm text-secondary">
        <span className="text-tertiary">{icon}</span>
        {label}
      </span>
      <span className="font-mono text-sm font-medium text-primary">{value}</span>
    </div>
  )
}

export function UserRisk() {
  const { id = '' } = useParams()
  const userId = Number(id)
  const [risk, setRisk] = useState<UserRiskData | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const ac = new AbortController()
    let alive = true
    setLoading(true)
    fetchUserRisk(userId, ac.signal)
      .then((r) => alive && setRisk(r))
      .catch(() => alive && setRisk(null))
      .finally(() => alive && setLoading(false))
    return () => {
      alive = false
      ac.abort()
    }
  }, [userId])

  const sig = risk?.signals

  return (
    <>
      <PageHeader
        title={S.title}
        screenId="A3.22"
        actions={
          <Link
            to={`/users/${id}`}
            className="inline-flex items-center gap-1 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:text-primary"
          >
            <ChevronLeft size={13} />
            {S.back}
          </Link>
        }
      />
      <div className="min-h-0 flex-1 overflow-auto p-3">
        {loading ? (
          <div className="p-6 text-center text-sm text-tertiary">…</div>
        ) : !risk ? (
          <div className="px-3 py-10 text-center text-sm text-tertiary">
            <ShieldAlert size={24} className="mx-auto mb-2 opacity-60" />—
          </div>
        ) : (
          <div className="mx-auto max-w-2xl space-y-3">
            <div className="flex items-center gap-5 rounded-lg border border-border-default bg-surface-2 p-4">
              <Gauge score={risk.score} level={risk.level} />
              <div className="space-y-2">
                <div className="text-xs uppercase tracking-wide text-tertiary">{S.level}</div>
                <span className={`inline-flex items-center rounded-full border px-3 py-1 text-sm font-semibold ${LEVEL_TONE[risk.level] ?? ''}`}>
                  {LEVEL_LABEL[risk.level] ?? risk.level}
                </span>
                <p className="max-w-xs pt-1 text-xs text-tertiary">{S.note}</p>
              </div>
            </div>

            <div className="rounded-lg border border-border-default bg-surface-2 p-4">
              <div className="mb-1 text-sm font-medium text-secondary">{S.signals}</div>
              <SignalRow icon={<Gavel size={14} />} label={S.strikePoints} value={sig?.active_strike_points ?? 0} />
              <SignalRow icon={<Flag size={14} />} label={S.reportsPending} value={sig?.reports_pending ?? 0} />
              <SignalRow icon={<Flag size={14} />} label={S.reportsTotal} value={sig?.reports_total ?? 0} />
              <SignalRow icon={<Users size={14} />} label={S.related} value={sig?.related_accounts ?? 0} />
              <SignalRow icon={<Ban size={14} />} label={S.banned} value={sig?.is_banned ? S.yes : S.no} />
            </div>
          </div>
        )}
      </div>
    </>
  )
}
